import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
  fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };

export default function MaintenanceFormModal({ record, vehicles, defaultVehicleId, onClose, onSubmit }) {
  const isEdit = !!record;
  const [vehicleId,      setVehicleId]      = useState(record?.vehicleId || defaultVehicleId || "");
  const [serviceType,    setServiceType]    = useState(record?.serviceType || "");
  const [dueDate,        setDueDate]        = useState(record?.dueDate ? record.dueDate.slice(0, 10) : "");
  const [dueOdometerKm,  setDueOdometerKm]  = useState(record?.dueOdometerKm || "");
  const [notes,          setNotes]          = useState(record?.notes || "");
  const [saving,         setSaving]         = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!vehicleId) return toast.error("Select a vehicle");
    if (!serviceType.trim()) return toast.error("Service type is required");
    setSaving(true);
    try {
      await onSubmit({
        vehicleId, serviceType: serviceType.trim(),
        dueDate:       dueDate || null,
        dueOdometerKm: dueOdometerKm ? Number(dueOdometerKm) : null,
        notes:         notes.trim() || null,
      });
      toast.success(isEdit ? "Maintenance record updated" : "Maintenance scheduled");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save maintenance record");
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 440,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              {isEdit ? "Edit Maintenance" : "Schedule Maintenance"}
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Vehicle *</label>
              <select
                style={{ ...inputStyle, cursor: isEdit ? "default" : "pointer" }}
                value={vehicleId}
                onChange={e => setVehicleId(e.target.value)}
                disabled={isEdit}
              >
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.nickname || v.plateNumber}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Service Type *</label>
              <input style={inputStyle} value={serviceType} onChange={e => setServiceType(e.target.value)} placeholder="e.g. Oil change, Tyre rotation" autoFocus />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Due Date</label>
                <input type="date" style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Due Odometer (km)</label>
                <input type="number" style={inputStyle} value={dueOdometerKm} onChange={e => setDueOdometerKm(e.target.value)} placeholder="Optional" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <input style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                marginTop: 6, width: "100%", height: 42, borderRadius: 10, border: "none",
                background: saving ? "#9CA3AF" : "#4F46E5",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : isEdit ? "Save Changes" : "Schedule"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
